import type { AdminOrgDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { tokens } from '../../../design';
import { formatDate } from '../../../utils/format';
import { placeText } from '../../../utils/place';
import { CompanyLogo } from '../_components/CompanyLogo';
import { CompanySourceBadge } from '../_components/CompanySourceBadge';
import { CompanyStatusBadge } from '../_components/CompanyStatusBadge';

/**
 * Columns for the companies grid (AUT-403), after `companies-list.html`:
 * Компания / УНП / Город / Владелец / Сотрудников / Статус / Источник /
 * Создана.
 *
 * Not here: «Отчётов за 30 дней» — company reports do not exist until
 * CP-05, and a column of zeros would read as «no work done». The owner
 * cell reads «код не активирован» while an owner code waits to be
 * claimed; the name arrives masked from the server without `view_pii`.
 */
type Col = GridColDef<AdminOrgDto>;

const Muted = ({ children }: { children: string }): ReactElement => (
  <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
    {children}
  </Typography>
);

const TextOrDash = ({
  value,
  t,
  mono = false,
}: {
  value: string | null;
  t: TFunction;
  mono?: boolean;
}): ReactElement =>
  value ? (
    <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined}>
      {value}
    </Typography>
  ) : (
    <Muted>{t('companies.noValue')}</Muted>
  );

const CompanyCell = ({ row, t }: { row: AdminOrgDto; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
    <CompanyLogo name={row.name} logoUrl={row.logoUrl} dimmed={row.status === 'terminated'} />
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {row.name}
        </Typography>
        {row.isDemo ? <Chip size="small" color="info" label={t('companies.demo')} /> : null}
      </Box>
      <Typography variant="caption" sx={{ color: tokens.text.tertiary }} noWrap>
        {row.legalName ?? t('companies.noLegalName')}
      </Typography>
    </Box>
  </Box>
);

export const OwnerCell = ({ org, t }: { org: AdminOrgDto; t: TFunction }): ReactElement => {
  if (org.owner?.name) return <Typography variant="body2">{org.owner.name}</Typography>;
  if (org.pendingOwnerCode) {
    return (
      <Chip size="small" color="warning" variant="outlined" label={t('companies.ownerPending')} />
    );
  }
  return <Muted>{t('companies.noValue')}</Muted>;
};

export const Seats = ({ active, max }: { active: number; max: number }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
    <Typography variant="body2" fontFamily="monospace">
      {active} / {max}
    </Typography>
    <LinearProgress
      variant="determinate"
      value={max > 0 ? Math.min(100, (active / max) * 100) : 0}
      sx={{ width: 40, height: 5, borderRadius: 999 }}
    />
  </Box>
);

export const buildCompanyColumns = (t: TFunction): Col[] => [
  {
    field: 'name',
    headerName: t('companies.columns.company'),
    flex: 1.6,
    minWidth: 240,
    sortable: false,
    renderCell: ({ row }) => <CompanyCell row={row} t={t} />,
  },
  {
    field: 'unp',
    headerName: t('companies.columns.unp'),
    flex: 0.7,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => <TextOrDash value={row.unp} t={t} mono />,
  },
  {
    field: 'city',
    headerName: t('companies.columns.city'),
    flex: 0.6,
    minWidth: 100,
    sortable: false,
    renderCell: ({ row }) => <TextOrDash value={placeText(row)} t={t} />,
  },
  {
    field: 'owner',
    headerName: t('companies.columns.owner'),
    flex: 1,
    minWidth: 160,
    sortable: false,
    renderCell: ({ row }) => <OwnerCell org={row} t={t} />,
  },
  {
    field: 'seats',
    headerName: t('companies.columns.seats'),
    flex: 0.7,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }) => <Seats active={row.activeMembers} max={row.maxMembers} />,
  },
  {
    field: 'status',
    headerName: t('companies.columns.status'),
    flex: 0.7,
    minWidth: 130,
    sortable: false,
    renderCell: ({ row }) => <CompanyStatusBadge status={row.status} />,
  },
  {
    field: 'source',
    headerName: t('companies.columns.source'),
    flex: 0.8,
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }) => <CompanySourceBadge source={row.source} />,
  },
  {
    field: 'createdAt',
    headerName: t('companies.columns.createdAt'),
    flex: 0.6,
    minWidth: 100,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" fontFamily="monospace">
        {formatDate(row.createdAt)}
      </Typography>
    ),
  },
];
